import React, { useCallback, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useSearchParams } from "react-router-dom";
import { Container } from "reactstrap";
import { getRapidKycSession } from "../../../services/kyc.service";
import Header from "../../Global/Header/Header";
import EmailCollectionForm from "../EmailCollectionForm/EmailCollectionForm";
import EmailVerificationForm from "../EmailVerificationForm/EmailVerificationForm";
import ExternalKycProviderConsentForm from "../ExternalKycProviderConsentForm/ExternalKycProviderConsentForm";
import ExternalKycProviderIFrameForm from "../ExternalKycProviderIFrameForm/ExternalKycProviderIFrameForm";
import FilboMobileRedirectHelper from "../FilboMobileRedirectHelper/FilboMobileRedirectHelper"; //this must come after import './KycFilboMobileRedirect.scss';
import { kycStore } from "../KycContext/KycContext";
import {
  kycAvailableSteps,
  KycForm,
  KycStep,
} from "../KycContext/kycAvailableSteps";
import PhoneAndEmailCollectionForm from "../PhoneAndEmailCollectionForm/PhoneAndEmailCollectionForm";
import PhoneNumberCollectionForm from "../PhoneNumberCollectionForm/PhoneNumberCollectionForm";
import PhoneNumberVerificationForm from "../PhoneNumberVerificationForm/PhoneNumberVerificationForm";
import ProgressBarLine from "../ProgressBarLine/ProgressBarLine";
import "./KycFilboMobileRedirect.scss";

interface FilboMobileQueryParams {
  redirect: string;
  providerSessionId: string;
  steps?: string;
}

export default function KycFilboMobileRedirect() {
  const {
    kycState,
    setKycState,
    startNewKycClientSession,
    continueKycClientSession,
    appInsightsTrackEvent,
  } = React.useContext(kycStore);

  const [searchParams] = useSearchParams();

  const attemptToContinueFilboMobileSession = useCallback(
    (query: FilboMobileQueryParams) => {
      appInsightsTrackEvent({
        name: "attemptToContinueFilboMobileSession starting",
        properties: {
          query: query,
        },
      });

      let kycSteps: KycStep[] | undefined = undefined;

      if (query.steps) {
        const stepsString: string = query.steps;

        kycSteps = [];
        const splitted = stepsString.split(" ");
        for (let index = 0; index < splitted.length; index++) {
          const kycStep = kycAvailableSteps.find(
            (s) => s.kycStep.toUpperCase() === splitted[index].toUpperCase()
          );

          if (kycStep) {
            kycSteps[index] = kycStep.kycStep;
          }
        }

        if (kycSteps.length > 0) {
          appInsightsTrackEvent({
            name: "attemptToContinueKycSession found steps in url",
            properties: {
              kycSteps: kycSteps,
            },
          });

          kycSteps.push(KycStep.FilboMobileRedirectHelper);
        }
      }

      getRapidKycSession(query.providerSessionId as string, "RO").then(
        (response) => {
          appInsightsTrackEvent({
            name: "KycFilboMobileRedirect getRapidKycSession - success",
            properties: {
              response: response,
            },
          });

          const urlsDict: { [id: string]: string } = {};

          urlsDict[KycForm.ExternalKycProviderIdScan] = response.data.ocrUrl;

          setKycState({
            contactInfoClientSessionId: response.data.contactInfoClientSessionId,
            contactInfoSessionId: response.data.contactInfoSessionId,
            kycProviderUrls: urlsDict,
            filboMobileRedirectUrl: query.redirect as string,
          });

          continueKycClientSession(
            response.data.contactInfoClientSessionId,
            response.data.contactInfoSessionId,
            kycSteps
          );
        }
      );
    },
    [
      appInsightsTrackEvent,
      setKycState,
      continueKycClientSession,
    ]
  );

  useEffect(() => {
    const redirect = searchParams.get("redirect");
    const providerSessionId = searchParams.get("providerSessionId");
    const steps = searchParams.get("steps");

    if (redirect && providerSessionId) {
      const query: FilboMobileQueryParams = {
        redirect,
        providerSessionId,
        ...(steps && { steps }),
      };
      attemptToContinueFilboMobileSession(query);
    } else {
      startNewKycClientSession();
    }
  }, [searchParams]);

  const getCurrentForm = () => {
    switch (kycState.currentForm) {
      case KycForm.PhoneNumberCollection:
        return <PhoneNumberCollectionForm />;
      case KycForm.PhoneNumberVerification:
        return <PhoneNumberVerificationForm />;
      case KycForm.EmailCollection:
        return <EmailCollectionForm />;
      case KycForm.EmailVerification:
        return <EmailVerificationForm />;
      case KycForm.ExternalKycProviderConsent:
        return <ExternalKycProviderConsentForm />;
      case KycForm.ExternalKycProviderSelfie:
      case KycForm.ExternalKycProviderLiveness:
      case KycForm.ExternalKycProviderIdScan:
        return <ExternalKycProviderIFrameForm />;
      case KycForm.FilboMobileRedirectHelper:
        return <FilboMobileRedirectHelper />;
      case KycForm.PhoneAndEmailCollection:
        return <PhoneAndEmailCollectionForm />;
    }
  };

  return (
    <React.Fragment>
      <Helmet>
        <title>Filbo Identity</title>
        <meta name="description" content="Identitatatea ta digitală" />
      </Helmet>
      <Header>Verificare cu selfie și carte de identitate</Header>

      <Container className="kyc-container">
        <ProgressBarLine
          className="kyc-prog-bar"
          percent={kycState.percent}
        ></ProgressBarLine>
        {getCurrentForm()}
      </Container>
    </React.Fragment>
  );
}
